import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { translations } from '../../../../locales/translations';
import { Accordion, AccordionDetails, AccordionSummary, Grid } from '@material-ui/core';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DisplayFieldVertical } from './DisplayFieldVertical';
import { DisplayBooleanVertical } from './DisplayBooleanVertical';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useSelector } from 'react-redux';
import { selectContractInfo, selectPreviewMode, selectContractInfoView } from '../slice/selectors';
import { LoadingIndicator } from '../../../components/LoadingIndicator';
import { DisplayFieldVerticalWithLink } from './DisplayFieldVerticalWithLink';

export const ContractInfoSection = (props) => {
  const { handleOnClickRenewed, setRenewedLink } = props;
  const { t } = useTranslation();
  const contractInfoData = useSelector(selectContractInfo);
  const contractInfoView = useSelector(selectContractInfoView);
  const previewMode = useSelector(selectPreviewMode);
  const [displayData, setDisplayData] = useState(contractInfoData);
  const [highlightChange, setHighlightChange] = useState(false);
  const [renewedOacl, setRenewedOacl] = useState('');

  React.useEffect(() => {
    if (previewMode && contractInfoView) {
      setHighlightChange(true);
      setDisplayData(contractInfoView);
    } else {
      setDisplayData(contractInfoData);
    }
  }, [previewMode]);

  React.useEffect(() => {
    if (displayData && displayData.renewedFromContract && displayData.renewedFromContract.length > 0) {
      setRenewedOacl(displayData.renewedFromContract.substring(6));
    } else {
      setRenewedOacl('');
    }
  }, [displayData]);

  React.useEffect(() => {
    if (!previewMode) {
      setDisplayData(contractInfoData);
    } else if (contractInfoView) {
      setDisplayData(contractInfoView);
    }
  }, [contractInfoData]);

  React.useEffect(() => {
    if (renewedOacl !== '') {
      setRenewedLink(`${process.env.PUBLIC_URL}/detail/${renewedOacl}`);
    } else {
      setRenewedLink('');
    }
  }, [renewedOacl]);

  return (
    <Wrapper>
      <Accordion defaultExpanded={true}>
        <AccordionSummary
          className='accordion-summary'
          expandIcon={<ExpandMoreIcon />}
          color='white'
        >
          <DescriptionOutlinedIcon />
          {t(translations.contractDetail.contractInformation.contractInfo)}
        </AccordionSummary>
        {displayData && contractInfoData ? (
          <AccordionDetails className='accordion-details'>
            <Grid container>
              <Grid item xs={12} md={6} lg={6} className='column'>
                <Grid container className='row'>
                  <Grid item xs={12} md={8} lg={8}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.contractInformation.customerName}
                      value={displayData.customerName}
                      displayChange={highlightChange && displayData.customerName !== contractInfoData.customerName}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} lg={4}>
                    <DisplayFieldVertical
                      label={
                        translations.contractDetail.contractInformation.customerCode}
                      value={displayData.customerCode}
                      displayChange={highlightChange && displayData.customerCode !== contractInfoData.customerCode}
                    />
                  </Grid>
                </Grid>
                <Grid container className='row'>
                  <Grid item xs={12} md={4} lg={4}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.contractInformation.signedDate}
                      value={displayData.signedDate}
                      displayChange={highlightChange && displayData.signedDate !== contractInfoData.signedDate}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} lg={4}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.contractInformation.termDate}
                      value={displayData.terminationDate}
                      displayChange={highlightChange && displayData.terminationDate !== contractInfoData.terminationDate}
                    />
                  </Grid>
                  <Grid item xs={12} md={3} lg={3}>
                    <DisplayBooleanVertical
                      value={displayData.isRenewed}
                      label={translations.contractDetail.contractInformation.isRenewed}
                      displayChange={highlightChange && displayData.isRenewed !== contractInfoData.isRenewed}
                    />
                  </Grid>
                </Grid>
                <Grid container className='row'>
                  <Grid item xs={12} md={4} lg={4}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.startDate}
                      value={displayData.startDate}
                      displayChange={highlightChange && displayData.startDate !== contractInfoData.startDate}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} lg={4}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.endDate}
                      value={displayData.endDate}
                      displayChange={highlightChange && displayData.endDate !== contractInfoData.endDate}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} lg={4}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.contractInformation.contractTerm}
                      value={displayData.contractTerm}
                      displayChange={highlightChange && displayData.contractTerm !== contractInfoData.contractTerm}
                    />
                  </Grid>
                </Grid>
                <Grid container className='row'>
                  <Grid item xs={12} md={4} lg={4}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.contractInformation.gfa}
                      value={displayData.GFA}
                      displayChange={highlightChange && displayData.GFA !== contractInfoData.GFA}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} lg={4}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.contractInformation.mrc}
                      value={displayData.mrcIncrementPercentage}
                      displayChange={highlightChange && displayData.mrcIncrementPercentage !== contractInfoData.mrcIncrementPercentage}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} lg={4}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.contractInformation.interestRate}
                      value={displayData.interestRate}
                      displayChange={highlightChange && displayData.interestRate !== contractInfoData.interestRate}
                    />
                  </Grid>
                </Grid>
                <Grid container className='row'>
                  <Grid item xs={12} md={4} lg={4}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.contractInformation.actualMoveOutDate}
                      value={displayData.actualMoveOutDate}
                      // new field if old data is undefined and new data is empty, don't consider it has change
                      displayChange={highlightChange
                        && (displayData.actualMoveOutDate ?? '') !== (contractInfoData.actualMoveOutDate ?? '')}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6} lg={6} className='column'>
                <Grid container className='row'>
                  <Grid item xs={12} md={12} lg={9}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.contractInformation.businessNature}
                      value={displayData.businessNature}
                      displayChange={highlightChange && displayData.businessNature !== contractInfoData.businessNature}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <DisplayBooleanVertical
                      value={displayData.existingCustomer}
                      label={translations.contractDetail.contractInformation.existingCustomer}
                      displayChange={highlightChange && displayData.existingCustomer !== contractInfoData.existingCustomer}
                    />
                  </Grid>
                </Grid>
                {displayData.renewedFromContract &&
                <Grid container className='row'>
                  <Grid item xs={12} md={12} lg={12}>
                    <DisplayFieldVerticalWithLink
                      label={translations.contractDetail.contractInformation.renewContract}
                      value={displayData.renewedFromContract} handleOnClick={handleOnClickRenewed}
                      hoverWording={`OA-CL-${renewedOacl}`}
                      displayChange={highlightChange && displayData.renewedFromContract !== contractInfoData.renewedFromContract}
                    />
                  </Grid>
                </Grid>
                }
                <Grid container className='row'>
                  <Grid item xs={12} md={4} lg={4}>
                    <DisplayBooleanVertical
                      value={displayData.isNovated}
                      label={translations.contractDetail.contractInformation.isNovated}
                      displayChange={highlightChange && typeof contractInfoData.isNovated !== 'undefined' && displayData.isNovated !== contractInfoData.isNovated}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} lg={4}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.contractInformation.submittedBy}
                      value={displayData.submittedBy}
                      // displayChange={highlightChange && displayData.submittedBy !== contractInfoData.submittedBy}
                    />
                  </Grid>
                </Grid>
                <Grid container className='row'>
                  <Grid item xs={12} md={12} lg={12}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.contractInformation.biRemark}
                      value={displayData.biRemark}
                      displayChange={highlightChange && displayData.biRemark !== contractInfoData.biRemark}
                    />
                  </Grid>
                </Grid>
                <Grid container className='row'>
                  {/* <Grid item xs={12} md={4} lg={4}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.contractInformation.signingEntity}
                      value={displayData.signingEntity}
                      displayChange={highlightChange && displayData.signingEntity !== contractInfoData.signingEntity}
                    />
                  </Grid> */}
                  <Grid item xs={12} md={4} lg={4}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.contractInformation.billingEntity}
                      value={displayData.billingEntity}
                      displayChange={highlightChange && displayData.billingEntity !== contractInfoData.billingEntity}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} lg={4}>
                    <DisplayFieldVertical
                      label={translations.contractDetail.contractInformation.quoteNumber}
                      value={displayData.quoteNumber}
                      displayChange={highlightChange && displayData.quoteNumber !== contractInfoData.quoteNumber}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </AccordionDetails>
        ) : (
          <LoadingIndicator />
        )}
      </Accordion>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;

  .MuiAccordionSummary-root {
    height: 50px;
  }

  .MuiAccordionSummary-root.Mui-expanded {
    min-height: 50px;
  }

  .accordion-summary {
    border-radius: 5px;
    font-size: 16px;
    color: ${p => p.theme.background};
    background-color: ${p => p.theme.primary};

    .MuiIconButton-label {
      color: white;
    }
  }

  .accordion-details {
    width: 100%;
    display: grid;

    .row {
      display: flex;
      align-items: flex-start;
      margin-bottom: 10px;
      flex-wrap: wrap;
    }
  }
`;
